import React, { useState } from 'react'
import styled from 'styled-components'
import { useWallet } from '../../components/WalletProvider'
import { useDemo } from '../../components/DemoProvider'
import WebhookSection from './components/WebhookSection'
import SDKSection from './components/SDKSection'
import APIKeysSection from './components/APIKeysSection'

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem 4rem;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
`

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 2rem;
`

const Sidebar = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1.5rem;
  position: sticky;
  top: 2rem;
  height: fit-content;
`

const MainContent = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 2rem;
`

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, var(--color-text) 0%, var(--color-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  grid-column: 1 / -1;
`

const Section = styled.div`
  margin-bottom: 2rem;
`

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
`

const EndpointCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`

const EndpointMethod = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Fira Code', monospace;
  font-size: 0.9rem;
  margin-right: 0.5rem;
  background: ${props => {
    switch (props.method) {
      case 'GET': return 'rgba(0, 150, 255, 0.2)';
      case 'POST': return 'rgba(0, 200, 100, 0.2)';
      case 'PUT': return 'rgba(255, 150, 0, 0.2)';
      case 'DELETE': return 'rgba(255, 50, 50, 0.2)';
      default: return 'rgba(150, 150, 150, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.method) {
      case 'GET': return '#00b8ff';
      case 'POST': return '#00e676';
      case 'PUT': return '#ffa726';
      case 'DELETE': return '#ff5252';
      default: return '#9e9e9e';
    }
  }};
`

const EndpointPath = styled.span`
  font-family: 'Fira Code', monospace;
  color: var(--color-text);
`

const TabList = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const Tab = styled.button`
  background: none;
  border: none;
  color: ${props => props.$active ? 'var(--color-primary)' : 'var(--color-text-secondary)'};
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 1rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.$active ? 'var(--color-primary)' : 'transparent'};
  }
`

const ConnectMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--color-text-secondary);
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`

const endpoints = [
  {
    method: 'POST',
    path: '/projects/create',
    description: 'Create a new project',
    example: {
      name: 'Website Development',
      description: 'Create a modern React website',
      agents: {
        coders: {
          count: 3,
          capabilities: ['frontend', 'react']
        }
      }
    }
  },
  {
    method: 'GET',
    path: '/projects/{id}',
    description: 'Get project details',
    example: {
      id: 'proj_123'
    }
  },
  {
    method: 'POST',
    path: '/agents/deploy',
    description: 'Deploy agents for a project',
    example: {
      project_id: 'proj_123',
      agent_type: 'coder',
      count: 3
    }
  }
]

export default function APIPage() {
  const { connected, WalletControl } = useWallet()
  const { isDemoMode, projectTemplates } = useDemo()
  const [activeTab, setActiveTab] = useState('keys')
  const [selectedEndpoint, setSelectedEndpoint] = useState(null)
  const [response, setResponse] = useState(null)

  // Consider user connected if they have a wallet or are in demo mode
  const isAuthorized = connected || isDemoMode

  const handleEndpointSelect = (endpoint) => {
    setSelectedEndpoint(endpoint)
    setResponse(null)
  }

  const handleTryIt = () => {
    // Simulate API call
    setTimeout(() => {
      setResponse({
        success: true,
        data: {
          id: 'proj_' + Math.random().toString(36).substr(2, 9),
          status: 'created',
          timestamp: new Date().toISOString()
        }
      })
    }, 1000)
  }

  const renderTabContent = () => {
    if (!isAuthorized) {
      return (
        <ConnectMessage>
          <div>Connect your wallet or enable Demo Mode to access the API tools</div>
          <WalletControl />
        </ConnectMessage>
      )
    }

    switch (activeTab) {
      case 'keys':
        return <APIKeysSection isAuthorized={isAuthorized} projects={projectTemplates} />
      case 'explorer':
        return (
          <>
            {selectedEndpoint ? (
              <Section>
                <EndpointCard>
                  <EndpointMethod method={selectedEndpoint.method}>
                    {selectedEndpoint.method}
                  </EndpointMethod>
                  <EndpointPath>{selectedEndpoint.path}</EndpointPath>
                  <p style={{ marginTop: '1rem' }}>{selectedEndpoint.description}</p>
                  
                  <SectionTitle>Request Body</SectionTitle>
                  <pre style={{ 
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '1rem',
                    borderRadius: '8px',
                    overflowX: 'auto'
                  }}>
                    {JSON.stringify(selectedEndpoint.example, null, 2)}
                  </pre>

                  <button 
                    onClick={handleTryIt}
                    style={{
                      background: 'var(--color-primary)',
                      color: 'var(--color-text)',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                      marginTop: '1rem'
                    }}
                  >
                    Try it out
                  </button>

                  {response && (
                    <div style={{ 
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '1rem',
                      borderRadius: '8px',
                      marginTop: '1rem'
                    }}>
                      <SectionTitle>Response</SectionTitle>
                      <pre>
                        {JSON.stringify(response, null, 2)}
                      </pre>
                    </div>
                  )}
                </EndpointCard>
              </Section>
            ) : (
              <p>Select an endpoint from the sidebar to try it out.</p>
            )}
          </>
        )
      case 'sdk':
        return <SDKSection isAuthorized={isAuthorized} />
      case 'webhooks':
        return <WebhookSection isAuthorized={isAuthorized} />
      default:
        return null
    }
  }

  return (
    <Container>
      <Content>
        <Title>API Documentation</Title>
        
        <Sidebar>
          <Section>
            <SectionTitle>Endpoints</SectionTitle>
            {endpoints.map((endpoint, index) => (
              <div 
                key={index}
                onClick={() => isAuthorized && handleEndpointSelect(endpoint)}
                style={{ 
                  cursor: isAuthorized ? 'pointer' : 'not-allowed',
                  marginBottom: '0.5rem',
                  opacity: isAuthorized ? 1 : 0.5
                }}
              >
                <EndpointMethod method={endpoint.method}>
                  {endpoint.method}
                </EndpointMethod>
                <EndpointPath>{endpoint.path}</EndpointPath>
              </div>
            ))}
          </Section>
        </Sidebar>

        <MainContent>
          <TabList>
            <Tab 
              $active={activeTab === 'keys'}
              onClick={() => setActiveTab('keys')}
            >
              API Keys
            </Tab>
            <Tab 
              $active={activeTab === 'explorer'}
              onClick={() => setActiveTab('explorer')}
            >
              API Explorer
            </Tab>
            <Tab 
              $active={activeTab === 'sdk'}
              onClick={() => setActiveTab('sdk')}
            >
              SDK Documentation
            </Tab>
            <Tab 
              $active={activeTab === 'webhooks'}
              onClick={() => setActiveTab('webhooks')}
            >
              Webhooks
            </Tab>
          </TabList>

          {renderTabContent()}
        </MainContent>
      </Content>
    </Container>
  )
}
